import { PartyCracker } from "@assets";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";
import LoadingPage from "@components/Loader/LoadingPage";
import { Player } from "@lottiefiles/react-lottie-player";
import { EventType, NFTEventInfo, NFTEventTier } from "@models/event";
import { DEFAULT_LANGUAGE, Language } from "@models/language";
import { TransactionStatus } from "@models/payment";
import { getRewardByTransactionId } from "@providers/checkout/reward";
import { useQuery } from "@tanstack/react-query";
import { groupBy, mapValues, sumBy } from "lodash";
import type { NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useUserStore } from "../../../auth";
import { backToEvent, usePaymentStore } from "../../payment";
import AnimatedCube from "./AnimatedCube/AnimatedCube";
import AnimatedTicket from "./AnimatedTicket/AnimatedTicket";
import CheckoutSuccessBenefit from "./CheckoutSuccessBenefit/CheckoutSuccessBenefit";
import CheckoutSuccessMembership from "./CheckoutSuccessMembership/CheckoutSuccessMembership";
import CheckoutSuccessTicket from "./CheckoutSuccessTicket/CheckoutSuccessTicket";
import { CheckoutSuccessContentProps } from "./types";

const _t = (key?: string) => key || "";

const LOADING_TIME = 2000;

const AnimatedBackground = () => {
  return (
    <Player
      autoplay
      loop
      background="transparent"
      speed={0.4}
      src={PartyCracker}
      style={{
        height: "100%",
        width: "100%",
        position: "fixed",
      }}
    />
  );
};

const CheckoutSuccess: NextPage = () => {
  const router = useRouter();
  const event = usePaymentStore((state) => state.event);
  const transaction = usePaymentStore((state) => state.transaction);
  const user = useUserStore((state) => state.user);

  const [language, setLanguage] = useState<Language>(DEFAULT_LANGUAGE);
  const [loading, setLoading] = useState(true);
  const [showTicket, setShowTicket] = useState(false);

  const hideAnimation = useMemo(
    () => router.query.static === "true",
    [router.query.static]
  );

  const [showAnimation, setShowAnimation] = useState(!hideAnimation);

  const tier: NFTEventTier | null = useMemo(() => {
    if (!event || !transaction) return null;
    try {
      return (
        event.tiers.find(
          (tier) => tier.id === transaction.items[0].productId
        ) || null
      );
    } catch (error) {
      return null;
    }
  }, [event, transaction]);

  const { data: reward } = useQuery(
    [`reward:${transaction?.id}`],
    () => getRewardByTransactionId(transaction!.id),
    {
      enabled: !!transaction && !!tier?.rewardConfig,
      refetchOnWindowFocus: false,
    }
  );

  const info: NFTEventInfo = useMemo(() => {
    if (!event) return new NFTEventInfo({ name: "" });
    console.log(event);
    return event.localeInfo(language);
  }, [event, language]);

  const itemsCount = useMemo(() => {
    if (!transaction) return {};
    /* return countBy(transaction.items, "name"); */
    return mapValues(groupBy(transaction.items, "name"), (group) =>
      sumBy(group, "quantity")
    );
  }, [transaction]);

  const itemsCurrency = useMemo(() => {
    if (!transaction) return {};
    return transaction.currency;
  }, [transaction]);

  const itemsFinalAmount = useMemo(() => {
    if (!transaction) return {};
    return transaction.finalAmount;
  }, [transaction]);

  useEffect(() => {
    if (!event || !transaction || !user) return;
    if (transaction.userId !== user.id) {
      console.log("not your transaction");
      backToEvent();
      return;
    } else {
      switch (transaction.status) {
        case TransactionStatus.FAILED: {
          router.push(
            `${window.location.pathname.replace("success", "cancel")}`
          );
          return;
        }
        case TransactionStatus.INITIAL: {
          router.push(`${window.location.pathname.replace("success", "")}`);
          return;
        }
      }
      setLoading(false);
    }
  }, [event, transaction?.status, user]);

  useLayoutEffect(() => {
    setTimeout(() => setShowTicket(true), LOADING_TIME);
  }, []);

  useEffect(() => {
    if (showAnimation || !event?.customizations?.redirectAfterSuccessUrl)
      return;
    setTimeout(
      () => {
        if (!event.customizations?.redirectAfterSuccessUrl?.startsWith("http"))
          return;
        console.log(
          "redirecting",
          event.customizations.redirectAfterSuccessUrl
        );
        window.location.href = event.customizations.redirectAfterSuccessUrl;
      },
      event?.customizations?.redirectAfterSuccessTimeout || 5000
    );
  }, [showAnimation, event?.customizations?.redirectAfterSuccessUrl]);

  if (
    loading ||
    !user ||
    !event ||
    !transaction ||
    [TransactionStatus.FAILED, TransactionStatus.INITIAL].includes(
      transaction.status
    )
  )
    return <LoadingPage />;

  let CheckoutSuccessContent: React.FC<CheckoutSuccessContentProps>;

  switch (event.type) {
    case EventType.BENEFIT:
      CheckoutSuccessContent = CheckoutSuccessBenefit;
      break;
    case EventType.MEMBERSHIP:
      CheckoutSuccessContent = CheckoutSuccessMembership;
      break;
    case EventType.TICKET:
    default:
      CheckoutSuccessContent = CheckoutSuccessTicket;
      break;
  }

  return (
    <>
      <CheckoutSuccessContent
        event={event}
        tier={tier}
        info={info}
        itemsCount={itemsCount}
        itemsCurrency={itemsCurrency}
        itemsFinalAmount={itemsFinalAmount}
        language={language}
        router={router}
        reward={reward}
      />
      {event.type === EventType.TICKET && (
        <Modal
          onClose={() => setShowAnimation(false)}
          isOpen={showAnimation}
          isCentered
          variant="transparent"
        >
          <ModalOverlay>{showTicket && <AnimatedBackground />}</ModalOverlay>
          <ModalContent>
            {showTicket && <ModalCloseButton color="white" />}
            <ModalBody>
              <div className="flex flex-col text-white ">
                {showTicket ? (
                  <>
                    <p className="text-lg mx-2 text-center ">
                      Congrats! You have secured your ticket(s) for the event!
                    </p>
                    <AnimatedTicket
                      event={event}
                      info={info}
                      itemsCount={itemsCount}
                      imageUrl={tier?.imageUrl || ""}
                    />
                    <a
                      className="text-center "
                      onClick={() => setShowAnimation(false)}
                    >
                      Click here to exit
                    </a>
                  </>
                ) : (
                  <>
                    <p className="text-center ">Confirming your order...</p>
                    <AnimatedCube />
                  </>
                )}
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
export default CheckoutSuccess;
